import get from 'lodash/get';

import {
  MockRecordResource,
  MockRecordSingleton,
  StorefrontResource,
  SwellStorefrontCollection,
  SwellStorefrontResource,
  SwellStorefrontRecord,
} from '@swell/apps-sdk';

import { getCookie } from 'utils/cookie';

/** @typedef {import('@swell/apps-sdk').Swell} Swell */
/** @typedef {import('@swell/apps-sdk').SwellTheme} SwellTheme */

export const PAGE_RECORD_QUERY = Object.freeze({
  active: { $ne: false },
  fields: ['id', 'name', 'slug', 'number', 'title', 'theme_template'],
  limit: 5,
});

export const PAGE_URL_PATTERN_REGEX = /:([\w._]+)/g;

function shouldFetchPageData(pageConfig) {
  if (!pageConfig) {
    return false;
  }

  return Boolean(pageConfig.url) && Boolean(pageConfig.json);
}

export function shouldFetchPageRecord(pageConfig) {
  if (!shouldFetchPageData(pageConfig)) {
    return false;
  }

  // Reset the regular expression's lastIndex before using .test()
  // because PAGE_URL_PATTERN_REGEX has the global 'g' flag.
  // With the 'g' flag, repeated calls to .test() on the same string
  // will advance the regex's internal search position, which can lead
  // to inconsistent results (true/false alternating).
  // Resetting lastIndex ensures each test starts from the beginning.
  PAGE_URL_PATTERN_REGEX.lastIndex = 0;

  return PAGE_URL_PATTERN_REGEX.test(pageConfig.url);
}

/**
 * The main purpose of the function is to set a name for the constructor,
 * since by the name of the construct we determine the type of resource.
 *
 * @param {object} parent class
 * @param {string} name resource
 */
function createResourceClass(parent, name) {
  // Create a new class that extends the parent class
  const NamedResourceClass = class extends parent {
    constructor(...args) {
      super(...args);

      Object.defineProperty(this, '_resourceName', {
        value: name,
      });
    }
  };

  // Define the name of class constructor
  Object.defineProperty(NamedResourceClass, 'name', {
    value: name,
  });

  return NamedResourceClass;
}

function getStorefrontResource(resource) {
  return createResourceClass(StorefrontResource, resource);
}

export function getMockResource(resource) {
  return createResourceClass(MockRecordResource, resource);
}

export function getMockSingleton(singleton) {
  return createResourceClass(MockRecordSingleton, singleton);
}

/**
 * @param {Swell} swell
 * @param {unknown} data
 */
function compilePageData(swell, data) {
  if (!data) {
    return data;
  }

  switch (typeof data) {
    case 'bigint':
    case 'boolean':
    case 'number':
    case 'string':
    case 'symbol':
      return data;

    default:
      break;
  }

  const compiled = {};

  for (const [key, item] of Object.entries(data)) {
    const isObject = typeof item === 'object' && item !== null && !item._swell;

    if (isObject) {
      switch (item?._type) {
        case 'SwellStorefrontCollection': {
          compiled[key] = new SwellStorefrontCollection(
            swell,
            data.id,
            {},
            () => compilePageData(swell, item.value),
          );
          break;
        }

        case 'SwellStorefrontRecord': {
          compiled[key] = new SwellStorefrontRecord(
            swell,
            item.value._collection,
            'all',
            {},
            () => compilePageData(swell, item.value),
          );
          break;
        }

        case 'StorefrontResource': {
          compiled[key] = new SwellStorefrontResource(
            swell,
            item.value._collection,
            () => compilePageData(swell, item.value),
          );
          break;
        }

        default: {
          if (item?._resource) {
            const Resource = getStorefrontResource(item._resource);
            compiled[key] = new Resource(() =>
              compilePageData(swell, item.value),
            );
          } else if (Array.isArray(item)) {
            compiled[key] = item.map((i) => compilePageData(swell, i));
          } else {
            compiled[key] = compilePageData(swell, item);
          }
          break;
        }
      }
    } else {
      compiled[key] = item;
    }
  }

  return compiled;
}

/**
 * @param {object} pageProps
 * @param {object} record
 * @returns {string}
 */
export function getPageUrl(pageProps, record) {
  const { url } = pageProps;

  if (!record) {
    return url;
  }

  return url.replace(PAGE_URL_PATTERN_REGEX, (match, p1) => {
    return get(record, p1) || match;
  });
}

/**
 * @template T
 * @param {string} storefrontUrl
 * @param {object} page
 * @param {SwellTheme} theme
 * @param {object} record
 * @returns {Promise<T>}
 */
function getPageData(storefrontUrl, page, theme, record) {
  const pageProps = theme.props.pages.find((p) => p.id === page.id);

  if (!shouldFetchPageData(pageProps)) {
    return null;
  }

  const pageUrl = getPageUrl(pageProps, record);
  const dataUrl = `${storefrontUrl}${pageUrl}.json`;

  return fetch(dataUrl, {
    headers: {
      'Swell-Data': JSON.stringify(getCookie('swell-data') || {}),
    },
  }).then((res) => res.json());
}

/**
 * @param {object} page
 * @param {SwellTheme} theme
 * @param {object} record
 */
function getPageProps(page, theme, record) {
  return {
    id: page.id,
    async getData(swell) {
      try {
        const pageData = await getPageData(
          swell.storefront_url,
          page,
          theme,
          record,
        );

        if (!pageData) {
          return {};
        }

        return compilePageData(swell, pageData);
      } catch (e) {
        return null;
      }
    },
  };
}

/**
 * @param {Swell} swell
 * @param {SwellTheme} theme
 * @param {object} page
 * @param {object} record
 * @param {string} [altTemplate]
 */
export async function getCanvasData(swell, theme, page, record, altTemplate) {
  const { id, getData } = getPageProps(page, theme, record);

  const pageData = getData && (await getData(swell, theme));

  let pageRecord;

  if (page.record) {
    pageRecord = get(pageData, page.record);
  }

  theme.globals = {};
  await theme.initGlobals(id, { pageRecord, altTemplate });

  if (pageData) {
    theme.setCompatibilityData(pageData);
  }

  let pageContent = await theme.renderPage({ ...pageData }, altTemplate);

  let pageProps = {};
  const layoutProps = {};

  const allSections = await theme.getAllSections();
  const pageSections = await theme.getPageSections(pageContent, false);
  const sectionGroups = await theme.getPageSectionGroups(id, altTemplate);
  const layoutSectionGroups = await theme.getLayoutSectionGroups(
    sectionGroups,
    false,
  );

  const themeGlobals = theme.globals;

  pageProps = {
    ...themeGlobals,
    ...pageData,
    pageProps,
  };

  return {
    pageProps,
    pageContent,
    layoutProps,
    allSections,
    pageSections,
    layoutSectionGroups,
    sectionGroups,
    themeGlobals,
    theme,
    pageId: page.id,
    record,
  };
}
